import { css } from '@emotion/react';
import {
  type ComponentProps,
  type EventHandler, type ReactNode, type Ref, type SyntheticEvent, useMemo,
} from 'react';
import { type TooltipBehavior } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type FaIcon, FontAwesomeIcon,
} from '../../icon/fontAwesomeIcon.component';
import { usePointerClickEvent } from '../../utils/usePointerClickEvent';
import { CSS_CLASSNAME } from './constants';
import {
  RoundButtonStyle, RoundButtonStyles,
} from './roundButton.styles';

export enum RoundButtonSize {
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
  XLarge = 'XLarge',
}

export const getRoundButtonSize = (size: RoundButtonSize) => {
  switch (size) {
    case RoundButtonSize.Small:
      return '25px';
    case RoundButtonSize.Medium:
      return '32px';
    case RoundButtonSize.Large:
      return '40px';
    case RoundButtonSize.XLarge:
    default:
      return '48px';
  }
};

export const getRoundButtonFontSize = (size: RoundButtonSize) => {
  switch (size) {
    case RoundButtonSize.Small:
      return '12px';
    case RoundButtonSize.Medium:
      return '16px';
    case RoundButtonSize.Large:
      return '18px';
    case RoundButtonSize.XLarge:
    default:
      return '24px';
  }
};

export const getHoverCss = ({ buttonStyle, theme, isDisabled }: {
  buttonStyle: RoundButtonStyle;
  isDisabled?: boolean;
  theme: Theme;
}) => {
  if (isDisabled) {
    return null;
  }

  const btnStyles = RoundButtonStyles(theme)[buttonStyle || RoundButtonStyle.Primary];
  return {
    background: btnStyles.backgroundHoverColor,
    border: `2px solid ${btnStyles.borderHoverColor}`,
    color: btnStyles.textHoverColor,
  };
};

const getButtonStyles = ({ buttonStyle, size, isDisabled }: {
  buttonStyle: RoundButtonStyle;
  size: RoundButtonSize;
  isDisabled?: boolean;
}) => (theme: Theme) => {
  const btnStyles = RoundButtonStyles(theme)[buttonStyle];

  return css({
    background: btnStyles.backgroundColor,
    border: btnStyles.border ?? 0,
    borderRadius: '50%',
    boxShadow: theme.roundButtonStyle.boxShadow,
    color: btnStyles.textColor,
    fontSize: getRoundButtonFontSize(size),
    lineHeight: 1,
    width: getRoundButtonSize(size),
    height: getRoundButtonSize(size),
    minWidth: getRoundButtonSize(size),
    minHeight: getRoundButtonSize(size),
    textAlign: 'center',
    padding: 0,
    '&:hover, &:focus-visible': getHoverCss({ buttonStyle, isDisabled, theme }),
  });
};

const iconStyle = css({
  margin: 0,
});

type RoundButtonDispatchProps = Readonly<{
  onClick?: EventHandler<SyntheticEvent>;
}>;

export type RoundButtonProps = RoundButtonStateProps & RoundButtonDispatchProps;

type RoundButtonStateProps = Readonly<{
  buttonStyle?: RoundButtonStyle;
  children?: ReactNode;
  className?: string;
  icon: FaIcon;
  isDisabled?: boolean;
  size?: RoundButtonSize;
  tooltipLabel?: ComponentProps<typeof TooltipComponent>['tooltipContent'];
  tooltipPlacement?: ComponentProps<typeof TooltipComponent>['placement'];
  tooltipDelayShow?: ComponentProps<typeof TooltipComponent>['delayShow'];
  tooltipBehavior?: TooltipBehavior;
  ref?: Ref<HTMLButtonElement>;
}>;

export const RoundButtonComponent = ({
  buttonStyle = RoundButtonStyle.Primary,
  children,
  className,
  icon,
  isDisabled,
  onClick,
  size = RoundButtonSize.Large,
  tooltipDelayShow,
  tooltipLabel,
  tooltipPlacement,
  tooltipBehavior,
  ...restProps
}: RoundButtonProps) => {
  const buttonClassname = useMemo(() => `${CSS_CLASSNAME} ${className || ''}`, [className]);

  const {
    onClick: handleClick,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
  } = usePointerClickEvent(onClick);

  return (
    <TooltipComponent
      delayShow={tooltipDelayShow}
      tooltipContent={tooltipLabel}
      behavior={tooltipBehavior}
      placement={tooltipPlacement}
    >
      <button
        {...restProps}
        type="button"
        css={getButtonStyles({
          buttonStyle,
          size,
          isDisabled,
        })}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        className={buttonClassname}
        disabled={isDisabled}
      >
        <FontAwesomeIcon
          css={iconStyle}
          icon={icon}
        />
        {children}
      </button>
    </TooltipComponent>
  );
};

RoundButtonComponent.displayName = 'RoundButtonComponent';
